import {
  type AudioRecorder as AudioRecorderInterface,
  createAudioRecorder,
} from "@bandwith/platform";
import {
  FloatingAudioControl,
  formatDuration,
  PrimaryButton,
  useGlobalPlayer,
} from "@bandwith/recordings";
import { Button, ConfirmingButton } from "@bandwith/ui";
import { Mic, Pause, Square, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

type RecorderState = "idle" | "recording" | "paused";

type Props = {
  /** Called when recording is saved */
  onSave: (file: File) => void;
  /** Whether the recorder trigger button is disabled */
  disabled?: boolean;
  /** Auto-start recording on mount */
  autoStart?: boolean;
};

/**
 * Audio recorder component with floating UI matching the global player.
 * Records audio and immediately saves when stopped (no preview step).
 */
export function AudioRecorder({ onSave, disabled, autoStart }: Props) {
  const [state, setState] = useState<RecorderState>("idle");
  const hasAutoStarted = useRef(false);
  const [duration, setDuration] = useState(0);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { close: closePlayer, isVisible: isPlayerVisible } = useGlobalPlayer(
    (s) => ({ close: s.close, isVisible: s.isVisible }),
  );

  const recorderRef = useRef<AudioRecorderInterface | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const levelUnsubRef = useRef<(() => void) | null>(null);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  // Initialize recorder on mount
  useEffect(() => {
    recorderRef.current = createAudioRecorder();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      // Clean up level subscription
      levelUnsubRef.current?.();
      // Cancel any in-progress recording on unmount
      recorderRef.current?.cancel();
    };
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    const recorder = recorderRef.current;
    if (!recorder) return;

    // Hide the player miniplayer before recording
    if (isPlayerVisible) {
      closePlayer();
    }

    try {
      await recorder.start();
      setState("recording");
      setDuration(0);

      // Subscribe to level updates
      const unsubLevel = recorder.onLevelUpdate((l) => {
        setLevel(l);
      });

      // Start duration timer using getDuration()
      timerRef.current = setInterval(() => {
        setDuration(Math.floor(recorder.getDuration()));
      }, 1000);

      // Store unsubscribe function for cleanup
      levelUnsubRef.current = unsubLevel;
    } catch (err) {
      console.error("Failed to start recording:", err);
      if (err instanceof DOMException && err.name === "NotAllowedError") {
        setError(
          "Microphone access denied. Please allow microphone access to record.",
        );
      } else {
        setError("Failed to start recording. Please try again.");
      }
    }
  }, [isPlayerVisible, closePlayer]);

  // Auto-start recording on mount if requested
  useEffect(() => {
    if (autoStart && !disabled && !hasAutoStarted.current) {
      hasAutoStarted.current = true;
      startRecording();
    }
  }, [autoStart, disabled, startRecording]);

  const pauseRecording = async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    await recorder.pause();
    setState("paused");
    setLevel(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const resumeRecording = async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    await recorder.resume();
    setState("recording");
    timerRef.current = setInterval(() => {
      setDuration(Math.floor(recorder.getDuration()));
    }, 1000);
  };

  const stopRecording = async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      const result = await recorder.stop();

      const blob = result.blob;
      if (!blob) {
        throw new Error("No blob data in recording result");
      }

      let ext = "audio";
      if (result.mimeType.includes("mp4")) ext = "m4a";
      else if (result.mimeType.includes("webm")) ext = "webm";
      const filename = `recording-${new Date().toISOString().slice(0, 19).replace(/[:-]/g, "")}.${ext}`;
      const file = new File([blob], filename, { type: result.mimeType });

      onSaveRef.current(file);

      URL.revokeObjectURL(result.uri);
    } catch (err) {
      console.error("Failed to stop recording:", err);
      setError("Failed to save recording. Please try again.");
    }

    setDuration(0);
    setLevel(0);
    setState("idle");
  };

  const cancelRecording = async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    await recorder.cancel();
    setDuration(0);
    setLevel(0);
    setState("idle");
  };

  const isVisible = state !== "idle";
  const isRecording = state === "recording" || state === "paused";

  // Always render the button - when recording, it becomes a Stop button
  return (
    <>
      <div className="flex flex-col gap-1">
        {isRecording ? (
          <Button
            variant="secondary"
            onClick={stopRecording}
            leftIcon={Square}
            leftIconClassName="text-red-400"
            className="w-full justify-center"
          >
            Stop
          </Button>
        ) : (
          <Button
            variant="secondary"
            onClick={startRecording}
            disabled={disabled}
            leftIcon={Mic}
            leftIconClassName="text-red-400"
            className="w-full justify-center"
          >
            Record
          </Button>
        )}
        {error && <span className="text-sm text-red-500">{error}</span>}
      </div>

      {/* Floating recorder UI when active */}
      <FloatingAudioControl
        isVisible={isVisible}
        primaryButton={
          <PrimaryButton
            icon={<Square className="h-5 w-5" aria-hidden="true" />}
            onClick={stopRecording}
            ariaLabel="Stop recording"
            variant="red"
          />
        }
        title={
          <span className="flex items-center gap-2">
            <LevelMeter level={state === "paused" ? 0 : level} />
            {state === "paused" ? "Paused" : "Recording..."}
          </span>
        }
        timeDisplay={formatDuration(duration)}
        meter={null}
        closeButton={
          <ConfirmingButton
            onConfirm={cancelRecording}
            confirmContent={<X className="h-4 w-4" aria-hidden="true" />}
            variant="ghost"
            size="icon-sm"
            className="shrink-0 text-text-muted transition-colors hover:bg-bg-tertiary hover:text-text-primary"
            aria-label="Discard recording"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </ConfirmingButton>
        }
        expandedContent={
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={state === "paused" ? resumeRecording : pauseRecording}
              leftIcon={state === "paused" ? Mic : Pause}
              leftIconClassName={
                state === "paused" ? "text-red-400" : undefined
              }
            >
              {state === "paused" ? "Resume" : "Pause"}
            </Button>
          </div>
        }
        testId="floating-recorder"
      />
    </>
  );
}

interface LevelMeterProps {
  /** Normalized level (0-1) */
  level: number;
}

/**
 * Audio level meter visualization.
 * Shows a dot that changes brightness based on audio input level.
 */
function LevelMeter({ level }: LevelMeterProps) {
  // Use sqrt to make it get bright faster at lower levels
  const adjustedLevel = Math.sqrt(level);

  // Interpolate from dark (#3a1111) to bright red (#ef4444)
  const r = Math.round(58 + adjustedLevel * (239 - 58));
  const g = Math.round(17 + adjustedLevel * (68 - 17));
  const b = Math.round(17 + adjustedLevel * (68 - 17));

  return (
    // biome-ignore lint/a11y/useSemanticElements: Custom circular meter styling requires div, <meter> can't achieve this visual
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: `rgb(${r}, ${g}, ${b})`,
      }}
      role="meter"
      aria-valuenow={Math.round(adjustedLevel * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Audio level"
    />
  );
}
